import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { InView } from 'react-intersection-observer'
import cx from 'classnames'
import _ from 'lodash'

import config from '../../../../../../config'

import FailedLoad from '../FailedLoad'
import BlockTitle from './components/BlockTitle'
import Collapse from '../../../../../../components/Collapse'

import hocConnect from './hocConnect'

import './style.scss'

export default function (module, options) {
    const o = _.merge(
        {
            levelReload: false,
            disableClose: false,
        },
        options
    )

    return (InnerComponent) => {
        @hocConnect(module)
        class Block extends Component {
            static propTypes = {
                blockAccessRightsPathFn: PropTypes.func.isRequired,
                blockKey: PropTypes.string.isRequired,
                blockSaveCustomData: PropTypes.func.isRequired,
                customData: PropTypes.object.isRequired,
                data: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
                disable: PropTypes.bool.isRequired,
                failed: PropTypes.bool.isRequired,
                id: PropTypes.string.isRequired,
                isActive: PropTypes.bool.isRequired,
                isBlockInView: PropTypes.func.isRequired,
                isLoaded: PropTypes.bool.isRequired,
                isLoading: PropTypes.bool.isRequired,
                level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
                loadBlockData: PropTypes.func.isRequired,
                loadedLocale: PropTypes.string,
                locale: PropTypes.string.isRequired,
                open: PropTypes.bool.isRequired,
                scrollRefs: PropTypes.object,
                scrollTo: PropTypes.func.isRequired,
                toggleBlock: PropTypes.func.isRequired,
                token: PropTypes.string,
                transformedData: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
            }

            componentDidMount() {
                const { blockKey, id, isActive, isLoading, loadBlockData, locale, loadedLocale, disable, level } =
                    this.props

                // if block is active and isn't loading and locale is different than loadedLocale and isn't disable
                if (isActive && !isLoading && locale !== loadedLocale && !disable) {
                    loadBlockData(module, id, blockKey, level)
                }
            }

            shouldComponentUpdate(nextProps) {
                if (this.props.level !== nextProps.level) return true
                if (this.props.open !== nextProps.open) return true
                if (this.props.blockKey !== nextProps.blockKey) return true
                if (this.props.isActive !== nextProps.isActive) return true
                if (this.props.isLoading !== nextProps.isLoading) return true
                if (this.props.isLoaded !== nextProps.isLoaded) return true
                if (this.props.disable !== nextProps.disable) return true
                if (this.props.id !== nextProps.id) return true
                if (this.props.locale !== nextProps.locale) return true
                if (this.props.loadedLocale !== nextProps.loadedLocale) return true
                if (this.props.token !== nextProps.token) return true
                if (!_.isEqual(this.props.customData, nextProps.customData)) return true
                if (!_.isEqual(this.props.transformedData, nextProps.transformedData)) return true
                if (!_.isEqual(this.props.data, nextProps.data)) return true

                return false
            }

            componentDidUpdate(prevProps) {
                const {
                    isActive: isActiveNext,
                    id: idNext,
                    isLoading: isLoadingNext,
                    locale: localeNext,
                    loadedLocale: loadedLocaleNext,
                    disable: disableNext,
                    level: levelNext,
                } = this.props
                const { isActive, blockKey, id, isLoading, loadBlockData, loadedLocale, disable, level } = prevProps

                // if block isn't loading and isn't disable
                if (!isLoading && !isLoadingNext && !disable && !disableNext) {
                    // and block state changing to active and loadedLocale is different
                    if (!isActive && isActiveNext && localeNext !== loadedLocaleNext) {
                        loadBlockData(module, idNext, blockKey, levelNext)
                    }

                    // and block is active and id of entity changed and loadedLocale is different
                    if (isActive && isActiveNext && id !== idNext && loadedLocale !== loadedLocaleNext) {
                        loadBlockData(module, idNext, blockKey, levelNext)
                    }

                    // and levelReload is enabled and block is active and id is same and level is different
                    if (o.levelReload && isActive && isActiveNext && id === idNext && level !== levelNext) {
                        loadBlockData(module, idNext, blockKey, levelNext)
                    }
                }
            }

            onTitleClickHandler = () => {
                const { disable, blockKey, id, isLoaded, toggleBlock } = this.props

                if (!disable && isLoaded && !o.disableClose) {
                    toggleBlock(module, id, blockKey)
                }
            }

            onSaveCustomDataHandler = (customData) => {
                const { blockKey, id, blockSaveCustomData } = this.props

                blockSaveCustomData(module, id, blockKey, customData)
            }

            renderAnimatedContent = (blockContent) => {
                const { id, open } = this.props

                return (
                    <Collapse
                        expand={open}
                        noAnimateId={id}
                        duration={1250}
                        dynamicTimeHeight={1250}
                        unMountOnClose={config.components.block.unmountClose}
                        nested
                    >
                        {blockContent}
                    </Collapse>
                )
            }

            blockInView = (inView) => {
                const { blockKey, isBlockInView } = this.props

                isBlockInView(inView, blockKey)
            }

            renderStaticContent = (blockContent) => {
                const { open } = this.props

                return <div className={cx('block-content', !open && 'hide')}>{blockContent}</div>
            }

            renderBlock = (component) => {
                if (module === config.modules.report.key) {
                    return (
                        <InView as="div" onChange={(inView) => this.blockInView(inView)}>
                            {component}
                        </InView>
                    )
                }

                return component
            }

            render() {
                const {
                    blockKey,
                    customData,
                    data,
                    id,
                    isActive,
                    level,
                    locale,
                    token,
                    isLoading,
                    transformedData,
                    loadBlockData,
                    blockAccessRightsPathFn,
                    failed,
                    open,
                    scrollTo,
                    scrollRefs,
                } = this.props

                if (!isActive) return null

                // Failed block data load
                if (failed) {
                    return (
                        <div
                            className={cx(
                                'block',
                                blockKey === config.modules.report.blocks.bisnodeCertificates.key && 'hidden'
                            )}
                        >
                            <BlockTitle
                                onClickHandler={this.onTitleClickHandler}
                                module={module}
                                id={id}
                                blockKey={blockKey}
                                level={level}
                                loadBlockData={loadBlockData}
                                blockAccessRightsPathFn={blockAccessRightsPathFn}
                                disableClose={o.disableClose}
                            />
                            <div className={cx('block-content', !open && 'hide')}>
                                <FailedLoad module={module} id={id} block={blockKey} level={level} show={failed} />
                            </div>
                        </div>
                    )
                }

                const blockContent = (
                    <div className={'block-content-wrap'}>
                        <InnerComponent
                            module={module}
                            data={data}
                            transformedData={transformedData}
                            customData={customData}
                            level={level}
                            locale={locale}
                            block={blockKey}
                            id={id}
                            token={token}
                            isLoading={isLoading}
                            scrollTo={scrollTo}
                            scrollRefs={scrollRefs}
                            saveCustomData={this.onSaveCustomDataHandler}
                        />
                    </div>
                )

                const content = this.renderStaticContent(blockContent)
                const wholeBlock = (
                    <>
                        <BlockTitle
                            onClickHandler={this.onTitleClickHandler}
                            module={module}
                            id={id}
                            blockKey={blockKey}
                            level={level}
                            loadBlockData={loadBlockData}
                            blockAccessRightsPathFn={blockAccessRightsPathFn}
                            disableClose={o.disableClose}
                        />
                        {!isLoading && content}
                    </>
                )

                return (
                    <div
                        className={cx(
                            'block',
                            blockKey === config.modules.report.blocks.bisnodeCertificates.key &&
                                _.isEmpty(data) &&
                                'hidden'
                        )}
                    >
                        {this.renderBlock(wholeBlock)}
                    </div>
                )
            }
        }

        return Block
    }
}
